/** This file must only contain pure code and pure imports */

import { type AssetType, type FlowGraphAssetType } from "core/FlowGraph/flowGraphAssetsContext";
import { type IFlowGraphBlockConfiguration } from "core/FlowGraph/flowGraphBlock";
import { type FlowGraphContext } from "core/FlowGraph/flowGraphContext";
import { type FlowGraphDataConnection } from "core/FlowGraph/flowGraphDataConnection.pure";
import { RichTypeAny, RichTypeString } from "core/FlowGraph/flowGraphRichTypes.pure";
import { FlowGraphBlockNames } from "../flowGraphBlockNames";
import { FlowGraphCachedOperationBlock } from "./flowGraphCachedOperationBlock";
import { RegisterClass } from "core/Misc/typeStore";

function CloneInvalidValue(value: unknown, copies?: Map<object, unknown>): unknown {
    if (value === null || (typeof value !== "object" && typeof value !== "function")) {
        return value;
    }
    const clonedValues = copies ?? new Map<object, unknown>();
    if (clonedValues.has(value)) {
        return clonedValues.get(value);
    }
    const clone = (value as { clone?: () => unknown }).clone;
    if (typeof clone === "function") {
        const result = clone.call(value);
        clonedValues.set(value, result);
        return result;
    }
    const prototype = Object.getPrototypeOf(value);
    if (!Array.isArray(value) && prototype !== Object.prototype && prototype !== null) {
        const result: unknown = structuredClone(value);
        clonedValues.set(value, result);
        return result;
    }
    const result: object = Array.isArray(value) ? new Array(value.length) : Object.create(prototype);
    clonedValues.set(value, result);
    for (const key of Reflect.ownKeys(value)) {
        const descriptor = Object.getOwnPropertyDescriptor(value, key)!;
        if ("value" in descriptor) {
            descriptor.value = CloneInvalidValue(descriptor.value, clonedValues);
        } else {
            descriptor.value = CloneInvalidValue(Reflect.get(value, key), clonedValues);
            delete descriptor.get;
            delete descriptor.set;
            descriptor.writable = true;
        }
        Object.defineProperty(result, key, descriptor);
    }
    return result;
}

/**
 * Configuration for the FlowGraphGetPropertyBlock.
 */
export interface IFlowGraphGetPropertyBlockConfiguration<O extends FlowGraphAssetType> extends IFlowGraphBlockConfiguration {
    /**
     * The name of the property that will be set
     */
    propertyName?: string;

    /**
     * The target asset from which the property will be retrieved
     */
    object?: AssetType<O>;

    /**
     * If true, the block will reset the output to the default value when the target asset is undefined.
     */
    resetToDefaultWhenUndefined?: boolean;

    /**
     * Optional value copied for each failed resolution and returned alongside isValid=false.
     * Scalars are retained, cloneable values use clone(), and arrays/plain records are copied recursively.
     * Other objects must support structured cloning.
     */
    invalidValue?: unknown;
}

/**
 * This block will deliver a property of an asset, based on the property name and an input asset.
 * The property name can include dots ("."), which will be interpreted as a path to the property.
 *
 * For example, with an input of a mesh asset, the property name "position.x" will deliver the x component of the position of the mesh.
 *
 * Note that it is recommended to input the object on which you are working on (i.e. a material) rather than providing a mesh as object and then getting the material from it.
 */
export class FlowGraphGetPropertyBlock<P extends any, O extends FlowGraphAssetType> extends FlowGraphCachedOperationBlock<P> {
    /**
     * Input connection: The asset from which the property will be retrieved
     */
    public readonly object: FlowGraphDataConnection<AssetType<O>>;

    /**
     * Input connection: The name of the property that will be set
     */
    public readonly propertyName: FlowGraphDataConnection<string>;

    /**
     * Input connection: A function that can be used to get the value of the property.
     * This will be used if defined, instead of the default get function.
     */
    public readonly customGetFunction: FlowGraphDataConnection<(target: AssetType<O>, propertyName: string, context: FlowGraphContext) => P | undefined>;

    /**
     * Constructs a new FlowGraphGetPropertyBlock.
     * @param config - the configuration of the block
     */
    constructor(
        /**
         * the configuration of the block
         */
        public override config: IFlowGraphGetPropertyBlockConfiguration<O>
    ) {
        super(RichTypeAny, config);
        this.object = this.registerDataInput("object", RichTypeAny, config.object);
        this.propertyName = this.registerDataInput("propertyName", RichTypeString, config.propertyName);
        this.customGetFunction = this.registerDataInput("customGetFunction", RichTypeAny);
    }

    /**
     * Retrieves the property value from the target object.
     * @param context - the flow graph context
     * @returns the property value, or undefined if the target or property name is not set
     */
    public override _doOperation(context: FlowGraphContext): P | undefined {
        const getter = this.customGetFunction.getValue(context);
        let value;
        if (getter) {
            value = getter(this.object.getValue(context), this.propertyName.getValue(context), context);
        } else {
            const target = this.object.getValue(context);
            const propertyName = this.propertyName.getValue(context);
            value = target && propertyName ? this._getPropertyValue(target, propertyName) : undefined;
        }
        return value;
    }

    private _getPropertyValue(target: AssetType<O>, propertyName: string): P | undefined {
        const path = propertyName.split(".");
        let value: any = target;
        for (const prop of path) {
            value = value[prop];
            if (value === undefined) {
                return;
            }
        }
        return value as P;
    }

    protected override _getInvalidOutputValue(context: FlowGraphContext): P {
        if (Object.prototype.hasOwnProperty.call(this.config, "invalidValue")) {
            return CloneInvalidValue(this.config.invalidValue) as P;
        }
        return super._getInvalidOutputValue(context);
    }

    /**
     * Returns the class name of this block.
     * @returns the class name
     */
    public override getClassName(): string {
        return FlowGraphBlockNames.GetProperty;
    }
}

let _Registered = false;
/**
 * Register side effects for flowGraphGetPropertyBlock.
 * Safe to call multiple times; only the first call has an effect.
 */
export function RegisterFlowGraphGetPropertyBlock(): void {
    if (_Registered) {
        return;
    }
    _Registered = true;

    RegisterClass(FlowGraphBlockNames.GetProperty, FlowGraphGetPropertyBlock);
}
